// The eval case inbox API (specs/api_contracts.md §11.7; TOM-140, ADR 0233),
// through the admin fetcher. Kept beside the Evals pages rather than in
// adminApi.ts, which the other Evals pages share.

import { adminApi } from '../adminApi';
import { queueQuery, type EvalQueueFilter } from './inbox';
import type {
  AdminBoatRow,
  AdminUserRow,
  EvalCandidateDetail,
  EvalCandidateList,
  EvalCaseExportOutcome,
  EvalConsentRevokeOutcome,
  EvalDraftSaveBody,
  EvalDraftSaveOutcome,
  EvalMomentConsent,
  EvalTriageDecision,
  EvalTriageOutcome,
} from './inboxTypes';

const CASES = '/v1/admin/evals/cases';
const casePath = (ref: string) => `${CASES}/${encodeURIComponent(ref)}`;

export const inboxApi = {
  /** The queue: content-free candidates with counts. */
  queue(filter: EvalQueueFilter, cursor?: string | null): Promise<EvalCandidateList> {
    return adminApi<EvalCandidateList>(`${CASES}${queueQuery(filter, cursor)}`);
  },
  /** One candidate with the reviewer's draft. `ref` is a candidate key or a row id. */
  candidate(ref: string): Promise<EvalCandidateDetail> {
    return adminApi<EvalCandidateDetail>(casePath(ref));
  },
  saveDraft(ref: string, body: EvalDraftSaveBody): Promise<EvalDraftSaveOutcome> {
    return adminApi<EvalDraftSaveOutcome>(`${casePath(ref)}/draft`, { method: 'PUT', body });
  },
  triage(ref: string, decision: EvalTriageDecision): Promise<EvalTriageOutcome> {
    return adminApi<EvalTriageOutcome>(`${casePath(ref)}/triage`, {
      method: 'POST',
      body: decision,
    });
  },
  /** Every accepted case not yet in the corpus, as the version-1 file. */
  exportAccepted(): Promise<EvalCaseExportOutcome> {
    return adminApi<EvalCaseExportOutcome>(`${CASES}/export`, { method: 'POST' });
  },
  consents(): Promise<{ consents: EvalMomentConsent[] }> {
    return adminApi<{ consents: EvalMomentConsent[] }>('/v1/admin/evals/consents');
  },
  grantConsent(body: {
    boatId: string;
    ownerUserId: string;
    corpusKey: string;
    basis: string;
  }): Promise<{ consent: EvalMomentConsent }> {
    return adminApi<{ consent: EvalMomentConsent }>('/v1/admin/evals/consents', {
      method: 'POST',
      body,
    });
  },
  revokeConsent(id: string): Promise<EvalConsentRevokeOutcome> {
    return adminApi<EvalConsentRevokeOutcome>(
      `/v1/admin/evals/consents/${encodeURIComponent(id)}/revoke`,
      { method: 'POST' }
    );
  },
  /** The fleet and the people, to name a consent's boat and owner. */
  boats(): Promise<{ boats: AdminBoatRow[] }> {
    return adminApi<{ boats: AdminBoatRow[] }>('/v1/admin/boats');
  },
  users(): Promise<{ users: AdminUserRow[] }> {
    return adminApi<{ users: AdminUserRow[] }>('/v1/admin/users');
  },
};
